import React, { useState, useEffect, useMemo } from "react";
import Autocomplete from "@mui/material/Autocomplete";
import {
  Box, Button, Typography, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, TableSortLabel, useTheme, Checkbox, IconButton, TextField, InputAdornment, MenuItem,
  Select, Chip, Avatar, Dialog, DialogTitle, DialogContent, Card,
  CardContent,
  CardActions, DialogActions,
} from "@mui/material";
import { Helmet } from "react-helmet";
import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import PendingActionsIcon from "@mui/icons-material/PendingActions";
import StarIcon from '@mui/icons-material/Star';
import StarHalfIcon from '@mui/icons-material/StarHalf';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import { useUser } from "../context/UserContext";
import ReviewsIcon from '@mui/icons-material/Reviews';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import Grid from '@mui/material/Grid';
import PageLoader from "../components/PageLoader";
import SectionLoader from "../components/SectionLoader";
import dayjs from "dayjs";

const formatFeedbackDate = (val) => {
  if (!val) return "N/A";
  const d = dayjs(val);
  return d.isValid() ? d.format("MMM DD, YYYY h:mm A") : val;
};

function StatCard({ label, count, IconComponent, active = false, onClick }) {
  return (
    <Card
      onClick={onClick}
      variant="outlined"
      sx={{
        height: "100%",
        minWidth: { xs: '100%', sm: 200 },
        width: "100%",
        borderRadius: 2.5,
        cursor: onClick ? 'pointer' : 'default',
        userSelect: 'none',
        transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
        background: active
          ? 'linear-gradient(135deg, rgba(76, 161, 175, 0.28) 0%, rgba(44, 62, 80, 0.5) 100%)'
          : 'rgba(255,255,255,0.05)',
        backdropFilter: 'blur(20px)',
        border: active
          ? '2px solid #4CA1AF'
          : '1px solid rgba(255,255,255,0.1)',
        boxShadow: active
          ? '0 8px 24px rgba(76, 161, 175, 0.35)'
          : '0 2px 8px rgba(0, 0, 0, 0.1)',
        position: 'relative',
        overflow: 'hidden',
        '&:hover': onClick ? {
          transform: 'translateY(-3px)',
          borderColor: active ? '#4CA1AF' : 'rgba(76, 161, 175, 0.5)',
          background: active
            ? 'linear-gradient(135deg, rgba(76, 161, 175, 0.35) 0%, rgba(44, 62, 80, 0.6) 100%)'
            : 'rgba(76, 161, 175, 0.12)',
          boxShadow: '0 8px 20px rgba(0, 0, 0, 0.3)',
        } : {},
      }}
    >
      <CardContent sx={{ minHeight: 80, p: '16px !important' }}>
        <Box display="flex" alignItems="center" sx={{ width: "100%" }}>
          <Box
            sx={{
              height: 48,
              width: 48,
              background: active ? '#4CA1AF' : 'rgba(76, 161, 175, 0.2)',
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
              flexShrink: 0,
              transition: 'all 0.25s ease',
              boxShadow: active ? '0 4px 12px rgba(76, 161, 175, 0.4)' : 'none',
            }}
          >
            <IconComponent sx={{ fontSize: 28, color: active ? '#FFFFFF' : '#4CA1AF' }} />
          </Box>

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box display="flex" alignItems="center" justifyContent="space-between" gap={1}>
              <Typography variant="h5" fontWeight="bold" noWrap sx={{ color: '#FFFFFF' }}>
                {count}
              </Typography>
              {active && (
                <Box
                  sx={{
                    px: 1,
                    py: 0.2,
                    borderRadius: 1,
                    bgcolor: 'rgba(76, 161, 175, 0.3)',
                    border: '1px solid rgba(76, 161, 175, 0.6)',
                    color: '#64d2e2',
                    fontSize: '0.65rem',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Active
                </Box>
              )}
            </Box>
            <Typography variant="body2" noWrap sx={{ color: active ? '#FFFFFF' : 'rgba(255,255,255,0.7)', fontWeight: active ? 600 : 400 }}>
              {label}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}


const Feedback = () => {
  const muiTheme = useTheme();
  const isLight = muiTheme.palette.mode === 'light';
  const [sortOrder, setSortOrder] = useState('asc');
  const [search, setSearch] = useState("");
  const [feedbackList, setFeedbackList] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [organization, setOrganization] = useState(null); // or orgOptions[0] for default
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const { user, isSwitched, effectiveOrgId } = useUser();
  const [AllEvents, SetAllEvents] = useState(0);
  const [UpcomingEvents, SetUpcomingEvents] = useState(0);
  const [ThisMonthEvents, SetThisMonthEvents] = useState(0);



  const [selected, setSelected] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState(null);

  const handleFeedbackClick = (feedback) => {
    setSelectedFeedback(feedback);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedFeedback(null);
  };
  const API_URL = process.env.REACT_APP_API_URL;

  const [loading, setLoading] = useState(true);


  const orgOptions = [{ id: 0, org_name: "All Organizations" }, ...organizations]; const handleSelectAll = (event) => {
    if (event.target.checked) {
      setSelected(feedbackList.map((feedback) => feedback.id));
    } else {
      setSelected([]);
    }
  };


  useEffect(() => {
    const fetchFeedbackList = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const targetOrg = effectiveOrgId || (organization?.id !== 0 ? organization?.id : null);
        const response = await fetch(`${API_URL}feedback-list`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            search: search,
            organization: targetOrg || null,
            page: page,
            pageSize: pageSize,
            fromDate: fromDate || undefined,
            toDate: toDate || undefined,
          })
        });
        const data = await response.json();

        if (!response.ok || data.success === false) {
          // 🔑 Handle invalid token
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
          }
        }
        setFeedbackList(data.data);
        setTotalItems(data.pagination.total);
        setTotalPages(data.pagination.totalPages);

      } catch (err) {
        // setError('Network error. Please try again.');
      } finally {
        setLoading(false);
      }
    };
    fetchFeedbackList();
  }, [organization, search, page, pageSize, fromDate, toDate]);

  useEffect(() => {
    const fetchRolesAndOrganizations = async () => {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}roles-organizations`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      const data = await response.json();

      if (!response.ok || data.success === false) {
          // 🔑 Handle invalid token
          if (data.message === 'Invalid token') {
            localStorage.removeItem('token');
            window.location.href = '/login';
            return;
          }
    }
      setOrganizations(data.organizations);

      SetAllEvents(data.allRating);
      SetUpcomingEvents(data.avgRating);
      SetThisMonthEvents(data.thismonthRating);


    };

    fetchRolesAndOrganizations();
  }, []);




  const [activeFilter, setActiveFilter] = useState('all'); // 'all' | 'top_rated' | 'this_week'

  const isThisWeek = (dateStr) => {
    if (!dateStr) return false;
    const d = new Date(dateStr);
    const now = new Date();
    const diffTime = Math.abs(now - d);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays <= 7;
  };

  const displayedFeedback = useMemo(() => {
    if (activeFilter === 'this_week') {
      return feedbackList.filter(f => isThisWeek(f.created_at));
    }
    if (activeFilter === 'top_rated') {
      return [...feedbackList].sort((a, b) => (b.rating || 0) - (a.rating || 0));
    }
    return feedbackList;
  }, [feedbackList, activeFilter]);

  const handleStatCardClick = (targetFilter) => {
    if (activeFilter === targetFilter) {
      setActiveFilter('all');
    } else {
      setActiveFilter(targetFilter);
    }
    setPage(1);
  };

  return (
    <>
      <Helmet>
        <title>Feedback - GPN</title>
      </Helmet>
      <Box>
        {/* Header */}
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
          <Box>
            <Typography variant="h5" fontWeight={700}>Feedback Management</Typography>
            <Typography variant="body2" color="text.secondary">
              View and manage feedback from members
            </Typography>
          </Box>
        </Box>

                {/* Compact Unified Toolbar */}
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 2.5,
            borderRadius: 2.5,
            background: isLight ? "#FFFFFF" : "rgba(255,255,255,0.04)",
            border: isLight ? "1px solid #E2E8F0" : "1px solid rgba(255,255,255,0.08)",
            backdropFilter: "blur(20px)",
          }}
        >
          {/* Row 1: Title, Count, Search, Sort & Actions */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1.5,
              pb: 1.25,
              borderBottom: isLight ? "1px solid #F1F5F9" : "1px solid rgba(255,255,255,0.06)",
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ color: isLight ? "#0F172A" : "#FFFFFF" }}>
                Feedback
              </Typography>
              <Chip
                label={`${feedbackList.length} Total`}
                size="small"
                sx={{
                  height: 22,
                  fontSize: "0.75rem",
                  fontWeight: 700,
                  bgcolor: isLight ? "rgba(13, 148, 136, 0.1)" : "rgba(76, 161, 175, 0.2)",
                  color: isLight ? "#0D9488" : "#64d2e2",
                }}
              />
            </Box>

            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <TextField
                size="small"
                placeholder="Search user or feedback..."
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: isLight ? "#64748B" : "rgba(255,255,255,0.6)", fontSize: 18 }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ width: { xs: "100%", sm: 220 }, "& .MuiInputBase-input": { py: 0.6, fontSize: "0.85rem" } }}
              />

              <Select
                size="small"
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                displayEmpty
                renderValue={(val) => `Sort: ${val === 'asc' ? 'A → Z' : val === 'desc' ? 'Z → A' : 'Newest'}`}
                sx={{ width: 130, "& .MuiSelect-select": { py: 0.6, fontSize: "0.85rem" } }}
              >
                <MenuItem value="asc">User: A → Z (Alphabetical)</MenuItem>
                <MenuItem value="desc">User: Z → A</MenuItem>
                <MenuItem value="newest">Date: Newest First</MenuItem>
              </Select>
            </Box>
          </Box>

          {/* Row 2: Filter Chips & Secondary Filters */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1.25,
              pt: 1.25,
            }}
          >
            {/* Filter Chips */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
              {[
                { key: "all", label: "All Feedback", count: AllEvents },
                { key: "top_rated", label: "Top Rated", count: UpcomingEvents },
                { key: "this_week", label: "This Week", count: ThisMonthEvents },
              ].map((tab) => {
                const isActive = activeFilter === tab.key;
                return (
                  <Chip
                    key={tab.key}
                    label={`${tab.label} (${tab.count})`}
                    size="small"
                    onClick={() => handleStatCardClick(tab.key)}
                    sx={{
                      cursor: "pointer",
                      fontWeight: isActive ? 700 : 500,
                      fontSize: "0.78rem",
                      height: 26,
                      bgcolor: isActive
                        ? (isLight ? "#0D9488" : "#4CA1AF")
                        : (isLight ? "#F1F5F9" : "rgba(255,255,255,0.06)"),
                      color: isActive ? "#FFFFFF" : (isLight ? "#475569" : "rgba(255,255,255,0.7)"),
                      "&:hover": {
                        bgcolor: isActive
                          ? (isLight ? "#0F766E" : "#3b8b98")
                          : (isLight ? "#E2E8F0" : "rgba(255,255,255,0.12)"),
                      },
                    }}
                  />
                );
              })}
            </Box>

            {/* Date Filters + Org */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
              <TextField
                size="small"
                type="date"
                placeholder="From"
                value={fromDate}
                onChange={(e) => {
                  setFromDate(e.target.value);
                  setPage(1);
                }}
                inputProps={{ max: "9999-12-31" }}
                sx={{ width: 125, "& .MuiInputBase-input": { py: 0.5, fontSize: "0.75rem" } }}
              />

              <TextField
                size="small"
                type="date"
                placeholder="To"
                value={toDate}
                onChange={(e) => {
                  setToDate(e.target.value);
                  setPage(1);
                }}
                inputProps={{ max: "9999-12-31" }}
                sx={{ width: 125, "& .MuiInputBase-input": { py: 0.5, fontSize: "0.75rem" } }}
              />

              {user?.role === 1 && !isSwitched && (
                <Autocomplete
                  size="small"
                  value={organization}
                  onChange={(event, newValue) => {
                    setOrganization(newValue);
                    setPage(1);
                  }}
                  options={orgOptions}
                  getOptionLabel={(option) => option?.org_name || ""}
                  renderInput={(params) => <TextField {...params} placeholder="Organization" />}
                  sx={{ width: 160, "& .MuiInputBase-input": { py: "2px !important", fontSize: "0.8rem" } }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                />
              )}
            </Box>
          </Box>
        </Paper>

        {/* Table */}
        <Paper>


          <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>




            <Table>
              <TableHead>
                <TableRow>

                  <TableCell>
                    <TableSortLabel
                      active={true}
                      direction={sortOrder}
                      onClick={() => setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc')}
                    >
                      User
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Rating</TableCell>
                  <TableCell>Feedback</TableCell>
                  <TableCell>Date</TableCell>
                  {user?.role === 1 && (
                    <TableCell>ORGANIZATION</TableCell>
                  )}
                  {/* <TableCell>STATUS</TableCell>
                <TableCell>ACTIONS</TableCell> */}
                </TableRow>
              </TableHead>
              <TableBody>


                {loading && (
                  <SectionLoader />
                )}

                {displayedFeedback.length > 0 ? (
                  displayedFeedback.map((feedback) => (
                    <TableRow
                      key={feedback.id}
                      hover
                      onClick={() => handleFeedbackClick(feedback)}
                      sx={{
                        cursor: 'pointer',
                        transition: 'background-color 0.15s ease',
                        '&:hover': {
                          bgcolor: isLight ? '#F0FDFA !important' : 'rgba(13, 148, 136, 0.1) !important',
                        },
                        '& td': {
                          borderBottom: isLight ? '1px solid #E2E8F0' : '1px solid rgba(255,255,255,0.06)',
                          py: 1.5,
                          color: isLight ? '#1E293B' : '#F1F5F9',
                        }
                      }}
                    >

                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            {feedback.app_users.profile_pic ? (
                              <Avatar
                                src={feedback.profile_pic}
                                alt={feedback.app_users.first_name + ' ' + feedback.app_users.last_name}
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 2,
                                  bgcolor: "#f3f4f6",
                                  color: "#2C3E50",
                                  fontWeight: 700,
                                  fontSize: 20,
                                }}
                                imgProps={{
                                  onError: (e) => {
                                    e.target.onerror = null;
                                    e.target.src = "";
                                  },
                                }}
                              >
                                {feedback.app_users.first_name?.[0]?.toUpperCase() || "?"}
                              </Avatar>
                            ) : (
                              <Avatar
                                sx={{
                                  width: 40,
                                  height: 40,
                                  borderRadius: 2,
                                  bgcolor: "#f3f4f6",
                                  color: "#2C3E50",
                                  fontWeight: 700,
                                  fontSize: 20,
                                }}
                              >
                                {feedback.app_users.first_name?.[0]?.toUpperCase() || "?"}
                              </Avatar>
                            )}
                          </Box>
                          <Box>
                            <Typography fontWeight={600}>
                              {feedback.app_users.first_name + ' ' + feedback.app_users.last_name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {feedback.app_users.email}
                            </Typography>

                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell>
                        {Array.from({ length: 5 }).map((_, index) => {
                          const full = Math.floor(feedback.rating);
                          const half = feedback.rating % 1 >= 0.5;
                          if (index < full) {
                            return <StarIcon key={index} style={{ color: '#FFD700' }} />;
                          } else if (index === full && half) {
                            return <StarHalfIcon key={index} style={{ color: '#FFD700' }} />;
                          } else {
                            return <StarBorderIcon key={index} style={{ color: '#FFD700' }} />;
                          }
                        })}
                      </TableCell>
                      <TableCell
                        onClick={() => handleFeedbackClick(feedback)}
                        sx={{ cursor: "pointer", maxWidth: "300px" }}
                        title="Click to view full feedback"
                      >
                        {feedback.feedback && feedback.feedback.length > 50
                          ? `${feedback.feedback.substring(0, 50)}...`
                          : feedback.feedback}
                      </TableCell>
                      <TableCell>{formatFeedbackDate(feedback.created_at)}</TableCell>

                      {user?.role === 1 && (
                        <TableCell>{feedback.origanisation.org_name}</TableCell>
                      )}



                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={7} sx={{ textAlign: "center" }}>
                      No Events found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>

            </Table>
          </TableContainer>

          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'block', md: 'none' }, p: 1, width: '100%' }}>
            <Grid container spacing={2} sx={{ width: '100%', m: 0 }}>
              {displayedFeedback.map((feedback) => (
                <Grid item xs={12} key={feedback.id}>
                  <Card
                    variant="outlined"
                    onClick={() => handleFeedbackClick(feedback)}
                    sx={{
                      borderRadius: 2,
                      cursor: 'pointer',
                      transition: 'transform 0.15s ease',
                      '&:hover': { transform: 'translateY(-2px)' }
                    }}
                  >
                    <CardContent sx={{ p: 3, textAlign: 'center' }}>
                      <Avatar
                        src={feedback.profile_pic}
                        sx={{ width: 60, height: 60, mx: 'auto', mb: 2, borderRadius: 2 }}
                      >
                        {feedback.app_users.first_name?.[0]?.toUpperCase() || "?"}
                      </Avatar>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                        {feedback.app_users.first_name} {feedback.app_users.last_name}
                      </Typography>
                      <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        {feedback.app_users.email}
                      </Typography>

                      <Box sx={{ display: 'flex', justifyContent: 'center', mb: 2 }}>
                        {Array.from({ length: 5 }).map((_, index) => {
                          const full = Math.floor(feedback.rating);
                          const half = feedback.rating % 1 >= 0.5;
                          if (index < full) return <StarIcon key={index} style={{ color: '#FFD700' }} />;
                          if (index === full && half) return <StarHalfIcon key={index} style={{ color: '#FFD700' }} />;
                          return <StarBorderIcon key={index} style={{ color: '#FFD700' }} />;
                        })}
                      </Box>

                      <Typography 
                        variant="body2" 
                        sx={{ mb: 2, fontStyle: 'italic', color: 'text.primary' }}
                        onClick={() => handleFeedbackClick(feedback)}
                      >
                        "{feedback.feedback}"
                      </Typography>

                      <Box sx={{ pt: 2, borderTop: '1px solid #E5E7EB', display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 1 }}>
                        <CalendarTodayIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
                        <Typography variant="caption" color="textSecondary">
                          {formatFeedbackDate(feedback.created_at)}
                        </Typography>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
              {feedbackList.length === 0 && (
                <Grid item xs={12}>
                  <Typography align="center" sx={{ py: 4, color: 'textSecondary' }}>No Feedback found</Typography>
                </Grid>
              )}
            </Grid>
          </Box>
          {/* Pagination */}
          {totalItems > 0 && (
            <>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", p: 2 }}>
                <Typography variant="body2">
                  Showing {page * pageSize - pageSize + 1}-{Math.min(page * pageSize, totalItems)} of {totalItems}
                </Typography>
                <Box>
                  <Button variant="outlined" sx={{ minWidth: 40, mx: 1 }} onClick={() => setPage(page - 1)} disabled={page === 1}>{'<'}</Button>
                  <Button variant="contained" sx={{ minWidth: 40, mx: 1, background: '#177373' }} onClick={() => setPage(page)}>{page}</Button>
                  <Button variant="outlined" sx={{ minWidth: 40, mx: 1 }} onClick={() => setPage(page + 1)} disabled={page === totalPages}>{'>'}</Button>
                </Box>
              </Box>

            </>

          )}
        </Paper>

        <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
          <DialogTitle>
            Feedback Details
            <IconButton
              aria-label="close"
              onClick={handleCloseDialog}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            {selectedFeedback && (
              <Box>
                <Box display="flex" alignItems="center" gap={2} mb={2}>
                  {selectedFeedback.app_users.profile_pic ? (
                    <Avatar
                      src={selectedFeedback.profile_pic}
                      alt={selectedFeedback.app_users.first_name + ' ' + selectedFeedback.app_users.last_name}
                    >
                      {selectedFeedback.app_users.first_name?.[0]?.toUpperCase() || "?"}
                    </Avatar>
                  ) : (
                    <Avatar>
                      {selectedFeedback.app_users.first_name?.[0]?.toUpperCase() || "?"}
                    </Avatar>
                  )}
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      {selectedFeedback.app_users.first_name} {selectedFeedback.app_users.last_name}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {selectedFeedback.app_users.email}
                    </Typography>
                  </Box>
                </Box>
                <Box mb={2}>
                  <Typography variant="subtitle2" gutterBottom>Rating:</Typography>
                  <Box display="flex">
                    {Array.from({ length: 5 }).map((_, index) => {
                      const full = Math.floor(selectedFeedback.rating);
                      const half = selectedFeedback.rating % 1 >= 0.5;
                      if (index < full) {
                        return <StarIcon key={index} style={{ color: '#FFD700' }} />;
                      } else if (index === full && half) {
                        return <StarHalfIcon key={index} style={{ color: '#FFD700' }} />;
                      } else {
                        return <StarBorderIcon key={index} style={{ color: '#FFD700' }} />;
                      }
                    })}
                  </Box>
                </Box>
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Feedback:</Typography>
                  <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                    {selectedFeedback.feedback}
                  </Typography>
                </Box>
                <Box mt={2}>
                  <Typography variant="caption" color="text.secondary">
                    Submitted on: {formatFeedbackDate(selectedFeedback.created_at)}
                  </Typography>
                </Box>
              </Box>
            )}
          </DialogContent>
        </Dialog>





      </Box>
    </>
  );
};

export default Feedback; 